import { gql, useQuery } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GenericShareHolderType } from '../../../../../gql/generated/graphql';

const SEARCH_GENERIC_SHAREHOLDERS = gql`
  query searchGenericShareholders($name: String) {
    searchGenericShareholders(name: $name) {
      edges {
        node {
          dbId
          name
          typeOfInvestor
          yearBornOrFounded
          isPe
          isNaturalPerson
        }
      }
    }
  }
`;

type ShareholderOption = Partial<GenericShareHolderType> & { inputValue?: string };

interface ShareholderAutocompleteProps {
  shareholderId: number | null;
  onSetShareholderId: (dbId: number) => void;
  onAddNewShareholder?: (name: string) => void;
  companyId?: number;
  newShareholderName?: string;
}

const ShareholderAutocomplete = ({
  shareholderId,
  onSetShareholderId,
  onAddNewShareholder,
  companyId,
  newShareholderName,
}: ShareholderAutocompleteProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<ShareholderOption[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedShareholder, setSelectedShareholder] = useState<ShareholderOption | null>(null);
  const [filteredOptions, setFilteredOptions] = useState<ShareholderOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const { loading, data } = useQuery(SEARCH_GENERIC_SHAREHOLDERS, {
    variables: {
      name: searchTerm || undefined,
    },
    skip: !searchTerm || searchTerm.length < 2,
  });

  useEffect(() => {
    if (data?.searchGenericShareholders?.edges) {
      const shareholders = data.searchGenericShareholders.edges.map(edge => edge.node).filter(Boolean);
      setOptions(shareholders);
    } else if (!searchTerm || searchTerm.length < 2) {
      setOptions([]);
    }
  }, [data, searchTerm]);

  useEffect(() => {
    if (newShareholderName && options.length > 0) {
      const shareholderByName = options.find(option => option.name?.toLowerCase() === newShareholderName.toLowerCase());

      if (shareholderByName && shareholderByName.dbId) {
        setSelectedShareholder(shareholderByName);
        setInputValue(shareholderByName.name || '');
        onSetShareholderId(shareholderByName.dbId);
      }
    }
  }, [newShareholderName, options, onSetShareholderId]);

  useEffect(() => {
    if (shareholderId && options.length > 0) {
      const shareholderById = options.find(option => option.dbId === shareholderId);
      if (shareholderById) {
        setSelectedShareholder(shareholderById);
        setInputValue(shareholderById.name || '');
      }
    } else if (!shareholderId) {
      setSelectedShareholder(null);
    }
  }, [shareholderId, options]);

  useEffect(() => {
    let filtered = options;

    if (inputValue) {
      const lowerCaseInput = inputValue.toLowerCase();
      filtered = options.filter(option => option.name?.toLowerCase().includes(lowerCaseInput));

      if (
        inputValue !== '' &&
        !filtered.some(option => option.name?.toLowerCase() === inputValue.toLowerCase()) &&
        onAddNewShareholder
      ) {
        filtered = [...filtered, { inputValue: `Add "${inputValue}"`, name: inputValue }];
      }
    }

    setFilteredOptions(filtered);
  }, [inputValue, options, onAddNewShareholder]);

  // Update search term when input changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handleOptionSelected = (_event: React.SyntheticEvent<Element, Event>, value: ShareholderOption | null) => {
    if (!value) {
      onSetShareholderId(0);
      setSelectedShareholder(null);
      return;
    }

    if (value.inputValue && value.inputValue.startsWith('Add "') && onAddNewShareholder) {
      onAddNewShareholder(value.name || '');
      return;
    }

    if (value.dbId) {
      onSetShareholderId(value.dbId);
      setSelectedShareholder(value);
    }
  };

  const getOptionLabel = (option: ShareholderOption) => {
    if (option.inputValue) {
      return option.inputValue;
    }
    return option.name || '';
  };

  return (
    <Autocomplete
      key={`shareholder-autocomplete-${shareholderId || 'new'}-${newShareholderName || 'none'}`}
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={(option, value) => {
        if (option.dbId && value.dbId) {
          return option.dbId === value.dbId;
        }
        return option.name?.toLowerCase() === value.name?.toLowerCase();
      }}
      getOptionLabel={getOptionLabel}
      options={filteredOptions}
      loading={loading}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      value={selectedShareholder}
      onChange={handleOptionSelected}
      renderInput={params => (
        <TextField
          {...params}
          label={t('Select Shareholder')}
          size="small"
          helperText="Type at least 2 characters to search for existing shareholders"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        if (option.inputValue && option.inputValue.startsWith('Add "')) {
          return (
            <li {...props}>
              <Box display="flex" alignItems="center">
                <AddIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography>{option.inputValue}</Typography>
              </Box>
            </li>
          );
        }

        return (
          <li {...props}>
            <Box display="flex" flexDirection="column">
              <Typography variant="body1">{option.name}</Typography>
              {(option.typeOfInvestor || option.yearBornOrFounded) && (
                <Typography variant="caption" color="text.secondary">
                  {[option.typeOfInvestor, option.yearBornOrFounded].filter(Boolean).join(' • ')}
                </Typography>
              )}
            </Box>
          </li>
        );
      }}
    />
  );
};

export default ShareholderAutocomplete;
